import { useCallback, useEffect, useState } from 'react';
import CopyOutlined from '@ant-design/icons/lib/icons/CopyOutlined';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';
import type { Dayjs } from 'dayjs';
import opConfig from 'openpathConfig';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { opTime } from 'utils/dates';
import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpEmpty } from 'new-components/DLS/OpEmpty/OpEmpty';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpRow } from 'new-components/DLS/OpRow/OpRow';
import { OpCol } from 'new-components/DLS/OpCol/OpCol';
import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpSwitch } from 'new-components/DLS/OpSwitch/OpSwitch';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';

import { getVisitorUrl } from '../../../../../altaLoginApp/helpers/getVisitorUrl';
import {
  DATE_TIME_AM_PM_FORMAT,
  DATE_TIME_FORMAT,
} from '../../../../constants/dates';
import STATUS from '../../../../constants/status';
import { triggerGuestPassUpdate } from '../../../../store/slices/guestPassSlice';
import { toLocalByTimezone, toUTCByTimezone } from '../../../../utils/dates';
import { VisitsState } from '../../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';

interface GuestPassEntry {
  id: number;
  name?: string;
}

interface GuestPassRecord {
  startUtc?: string | null;
  endUtc?: string | null;
  PIN?: string | null;
  urlToken?: string;
  msiAltaEntryId?: GuestPassEntry[];
  autoSignIn?: number | boolean;
}

interface GuestPassData extends Omit<GuestPassRecord, 'startUtc' | 'endUtc'> {
  startUtc?: Dayjs;
  endUtc?: Dayjs;
  startUtcRaw?: string;
  endUtcRaw?: string;
  msiAltaEntryId: GuestPassEntry[];
}

interface GuestPassResponse {
  data?: GuestPassRecord[] | null;
}

interface IntegrationMsiAltaConfig {
  guestPassPINDefault?: number;
  entryIds?: string[];
}

interface IntegrationMsiAltaResponse {
  data?: IntegrationMsiAltaConfig[] | null;
}

interface EntryDetailResponse {
  data?: Array<{ id: number; name: string }> | null;
}

interface GuestPassFormValues {
  startUtc: Dayjs | string | null | undefined;
  endUtc: Dayjs | string | null | undefined;
  entryId: number[];
  guestPassPINDefault?: boolean;
  autoSignIn?: boolean;
}

const GuestPassTab = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch<AppDispatch>();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const { hasWritePermission: hasGuestpassWrite } = useEndpointPermissions(
    'updateMsiAltaGuestPass',
  );
  // Only fetch the MSI Alta integration when the user has a scope helium's
  // GET /integrationMsiAlta route accepts, otherwise it 403s ("method that
  // get a integration").
  const { hasReadPermission: hasMsiAltaIntegrationRead } =
    useEndpointPermissions('getIntegrationMsiAlta');

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];

  const tz = selectedVisit?.location?.timezone;

  const [guestPassData, setGuestPassData] = useState<GuestPassData | undefined>(
    undefined,
  );
  const [guestPassPINDefault, setGuestPassPINDefault] =
    useState<boolean>(false);
  const [defaultEntryIds, setDefaultEntryIds] = useState<number[]>([]);
  const [entryIdOptions, setEntryIdOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [isGenerateGuestPassModalOpen, setIsGenerateGuestPassModalOpen] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [generateguestPassForm] = OpForm.useForm();
  const [guestPassForm] = OpForm.useForm();

  const confirmModal = useConfirmModal();

  const apiUrl = getVisitorUrl(opConfig.ENV);

  const normalizeGuestPass = useCallback(
    (guestPass: GuestPassRecord): GuestPassData => {
      const startLocal = guestPass.startUtc
        ? toLocalByTimezone(guestPass.startUtc, tz)
        : null;
      const endLocal = guestPass.endUtc
        ? toLocalByTimezone(guestPass.endUtc, tz)
        : null;

      return {
        ...guestPass,
        startUtc: startLocal
          ? opTime(startLocal.format('YYYY-MM-DD HH:mm:ss'))
          : undefined,
        endUtc: endLocal
          ? opTime(endLocal.format('YYYY-MM-DD HH:mm:ss'))
          : undefined,
        startUtcRaw: guestPass.startUtc || undefined,
        endUtcRaw: guestPass.endUtc || undefined,
        msiAltaEntryId: Array.isArray(guestPass.msiAltaEntryId)
          ? guestPass.msiAltaEntryId
          : [],
      };
    },
    [tz],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!hasMsiAltaIntegrationRead) return;
      setLoading(true);
      try {
        const integrationData = (await heliumReceive('getIntegrationMsiAlta', [
          orgId,
        ])) as IntegrationMsiAltaResponse;
        const initialData = integrationData.data?.[0];
        setGuestPassPINDefault(initialData?.guestPassPINDefault === 1);
        if (Array.isArray(initialData?.entryIds)) {
          setDefaultEntryIds(
            initialData.entryIds.map((id: string) => Number(id)),
          );
        }

        const guestPass = (await heliumReceive('getMsiAltaGuestPass', [
          orgId,
          selectedVisit?.id,
        ])) as GuestPassResponse;
        if (Array.isArray(guestPass.data) && guestPass.data.length !== 0) {
          setGuestPassData(normalizeGuestPass(guestPass.data[0]));
        }

        const listAllEntriesResponse = (await heliumReceive(
          'getMsiAltaEntryDetails',
          [orgId],
        )) as EntryDetailResponse;
        const defaultEntryList =
          listAllEntriesResponse.data?.map((defaultEntry) => ({
            value: defaultEntry.id,
            label: defaultEntry.name,
          })) || [];
        setEntryIdOptions(defaultEntryList);
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };

    if (orgId && selectedVisit?.id) {
      fetchData();
    }
  }, [
    normalizeGuestPass,
    orgId,
    selectedVisit?.id,
    tz,
    hasMsiAltaIntegrationRead,
  ]);

  const handleSubmit = async ({
    values,
  }: OnSubmitArgs<GuestPassFormValues>) => {
    setIsSubmitting(true);
    try {
      // update
      if (guestPassData !== undefined) {
        const submittingValues = {
          startUtc: toUTCByTimezone(values.startUtc, tz)?.format(
            DATE_TIME_FORMAT,
          ),
          endUtc: toUTCByTimezone(values.endUtc, tz)?.format(DATE_TIME_FORMAT),
          entryId: values.entryId,
          PIN:
            values.guestPassPINDefault === true
              ? guestPassData.PIN === null || guestPassData.PIN === ''
                ? Math.floor(1000 + Math.random() * 9000).toString()
                : guestPassData.PIN
              : '',
          autoSignIn: values.autoSignIn ? 1 : 0,
        };
        if (
          submittingValues.endUtc &&
          submittingValues.startUtc &&
          opTime(submittingValues.endUtc).isBefore(
            opTime(submittingValues.startUtc),
          )
        ) {
          notificationService.show(
            'error',
            t('visitors.guestPass.endBeforeStartError'),
          );
          return;
        }

        // Edit Guest Pass
        await heliumSend(
          'updateMsiAltaGuestPass',
          [orgId, selectedVisit?.id],
          submittingValues,
        );
      } else {
        // create
        const submittingValues = {
          startUtc: toUTCByTimezone(values.startUtc, tz)?.format(
            DATE_TIME_FORMAT,
          ),
          endUtc: toUTCByTimezone(values.endUtc, tz)?.format(DATE_TIME_FORMAT),
          entryId: values.entryId,
          userId: globalUserId,
          PIN:
            values.guestPassPINDefault === true
              ? Math.floor(1000 + Math.random() * 9000).toString()
              : undefined,
          autoSignIn: values.autoSignIn ? 1 : 0,
        };
        if (
          submittingValues.endUtc &&
          submittingValues.startUtc &&
          opTime(submittingValues.endUtc).isBefore(
            opTime(submittingValues.startUtc),
          )
        ) {
          notificationService.show(
            'error',
            t('visitors.guestPass.endBeforeStartError'),
          );
          return;
        }

        // Generate Guest Pass
        await heliumSend(
          'newMsiAltaGuestPass',
          [orgId, selectedVisit?.id],
          submittingValues,
        );
      }

      const guestPass = (await heliumReceive('getMsiAltaGuestPass', [
        orgId,
        selectedVisit?.id,
      ])) as GuestPassResponse;

      if (
        !guestPass ||
        !Array.isArray(guestPass.data) ||
        guestPass.data.length === 0
      ) {
        setGuestPassData(undefined);
        notificationService.show(
          'error',
          t('visitors.guestPass.submissionError'),
        );
        return;
      }

      const modifiedGuestPass = normalizeGuestPass(guestPass.data[0]);

      setGuestPassData(modifiedGuestPass);
      notificationService.show(
        'success',
        t('visitors.guestPass.updateSuccess'),
      );
      dispatch(triggerGuestPassUpdate()); // inform guest pass is updated for guest pass report page
    } catch {
      notificationService.show(
        'error',
        t('visitors.guestPass.submissionError'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const finalOnCancel = () => {
    if (generateguestPassForm.isFieldsTouched()) {
      confirmModal({
        title: t('confirmDiscard.title'),
        content: t('confirmDiscard.message'),
        okText: t('confirmDiscard.ok'),
        cancelText: t('confirmDiscard.cancel'),
        onOk: () => {
          generateguestPassForm.resetFields();
          setIsGenerateGuestPassModalOpen(false);
        },
      });
    } else {
      setIsGenerateGuestPassModalOpen(false);
    }
  };

  const handleRevoke = () => {
    try {
      confirmModal({
        title: t('visitors.guestPass.confirmRevoke.title'),
        content: t('visitors.guestPass.confirmRevoke.message'),
        okText: t('visitors.guestPass.confirmRevoke.ok'),
        cancelText: t('visitors.guestPass.confirmRevoke.cancel'),
        onOk: async () => {
          await heliumSend(
            'revokeMsiAltaGuestPass',
            [orgId, selectedVisit?.id],
            {},
          );
          notificationService.show(
            'success',
            t('visitors.guestPass.revokeSuccess'),
          );

          setGuestPassData(undefined); // Reset the guest pass data
          dispatch(triggerGuestPassUpdate()); // inform guest pass is updated for guest pass report page
        },
      });
    } catch {
      // Failed to revoke guest pass
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        notificationService.show(
          'success',
          t('visitors.guestPass.copySuccess'),
        );
      })
      .catch(() => {
        notificationService.show('error', t('visitors.guestPass.copyFail'));
      });
  };

  const isGuestPassActive = useCallback(() => {
    if (guestPassData?.startUtcRaw && guestPassData?.endUtcRaw) {
      const now = opTime.utc();
      return (
        now.isAfter(opTime.utc(guestPassData.startUtcRaw)) &&
        now.isBefore(opTime.utc(guestPassData.endUtcRaw))
      );
    }
    return false;
  }, [guestPassData]);

  const hasValidVisitor = selectedVisit?.visitors?.some(
    (visitor) =>
      visitor.status !== STATUS.DENIED_ENTRY.id &&
      visitor.status !== STATUS.SIGNED_OUT.id,
  );

  const getDefaultGuestPassTime = () => {
    const nowInTz = toLocalByTimezone(opTime().utc().format(), tz) || opTime();
    const scheduleStartInTz = selectedVisit?.scheduleStart
      ? toLocalByTimezone(selectedVisit.scheduleStart, tz)
      : null;
    const scheduleEndInTz = selectedVisit?.scheduleEnd
      ? toLocalByTimezone(selectedVisit.scheduleEnd, tz)
      : null;

    const now = nowInTz
      ? opTime(nowInTz.format('YYYY-MM-DD HH:mm:ss'))
      : opTime();
    const scheduleStart = scheduleStartInTz
      ? opTime(scheduleStartInTz.format('YYYY-MM-DD HH:mm:ss'))
      : null;
    const scheduleEnd = scheduleEndInTz
      ? opTime(scheduleEndInTz.format('YYYY-MM-DD HH:mm:ss'))
      : null;

    const statusId = selectedVisit?.visitStatus?.id;

    if (statusId === STATUS.PENDING.id) {
      if (scheduleStart && now.isBefore(scheduleStart)) {
        return {
          startUtc: scheduleStart,
          endUtc: scheduleEnd || scheduleStart.add(1, 'hour'),
        };
      }
      if (
        scheduleStart &&
        scheduleEnd &&
        now.isAfter(scheduleStart) &&
        now.isBefore(scheduleEnd)
      ) {
        return { startUtc: now, endUtc: scheduleEnd };
      }
      if (scheduleEnd && now.isAfter(scheduleEnd)) {
        return { startUtc: now, endUtc: now.add(1, 'hour') };
      }
    } else if (statusId === STATUS.SIGNED_IN.id) {
      if (scheduleStart && scheduleEnd && now.isBefore(scheduleEnd)) {
        return { startUtc: now, endUtc: scheduleEnd };
      }
      return { startUtc: now, endUtc: now.add(1, 'hour') };
    } else if (statusId === STATUS.SIGNED_OUT.id) {
      return { startUtc: now, endUtc: now.add(1, 'hour') };
    }
    return { startUtc: now, endUtc: now.add(1, 'hour') }; // Default case
  };

  const defaultGuestPassTime = getDefaultGuestPassTime();

  if (loading || isSubmitting) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <OpSpin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  return (
    <>
      {guestPassData === undefined ? (
        hasGuestpassWrite && hasValidVisitor ? (
          <OpButton
            type="primary"
            ghost
            style={{ width: '100%' }}
            onClick={() => {
              setIsGenerateGuestPassModalOpen(true);
            }}
          >
            {t('visitors.guestPass.generate')}
          </OpButton>
        ) : (
          <OpEmpty description={t('visitors.guestPass.empty')} />
        )
      ) : (
        <OpForm
          form={guestPassForm}
          onSubmit={() => {}}
          initialValues={guestPassData}
          hasError={false}
          defaultButtons={false}
          isReadOnly
        >
          {!isGuestPassActive() && (
            <div
              style={{
                backgroundColor: 'grey',
                color: 'white',
                padding: '10px 0',
                textAlign: 'center',
                borderRadius: '16px',
                fontWeight: 'bold',
                marginBottom: '16px',
              }}
            >
              {t('visitors.guestPass.status.invalid')}
            </div>
          )}

          {isGuestPassActive() && (
            <div
              style={{
                backgroundColor: 'var(--colorSuccessActive)',
                color: 'var(--colorWhite)',
                padding: '10px 0',
                textAlign: 'center',
                borderRadius: '16px',
                fontWeight: 'bold',
                marginBottom: '16px',
              }}
            >
              {t('visitors.guestPass.status.valid')}
            </div>
          )}

          <OpRow gutter={24}>
            <OpCol span={12}>
              <OpForm.DatePicker
                format={DATE_TIME_AM_PM_FORMAT}
                showTime
                minuteStep={5}
                label={t('visitors.guestPass.validFrom')}
                name="startUtc"
                rules={[
                  {
                    required: true,
                    message: t(
                      'visitors.guestPass.validation.startDateRequired',
                    ),
                  },
                ]}
              />
            </OpCol>
            <OpCol span={12}>
              <OpForm.DatePicker
                format={DATE_TIME_AM_PM_FORMAT}
                showTime
                minuteStep={5}
                label={t('visitors.guestPass.validTo')}
                name="endUtc"
                rules={[
                  {
                    required: true,
                    message: t('visitors.guestPass.validation.endDateRequired'),
                  },
                ]}
              />
            </OpCol>
          </OpRow>
          <OpList
            header={t('visitors.guestPass.url')}
            dataSource={[{ label: t('visitors.guestPass.url') }]}
            bordered
            renderItem={() => (
              <OpList.Item>
                <div style={{ width: '100%' }}>
                  {/* Display Guest Pass URL */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <a
                      href={`${apiUrl}/nl/guestpass/${guestPassData.urlToken}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'var(--colorPrimary)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      {`${apiUrl}/nl/guestpass/${guestPassData.urlToken}`}
                    </a>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(
                          guestPassData.PIN
                            ? `${apiUrl}/nl/guestpass/${guestPassData.urlToken}\nPIN: ${guestPassData.PIN}`
                            : `${apiUrl}/nl/guestpass/${guestPassData.urlToken}`,
                        )
                      }
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: 'var(--colorPrimary)',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                      }}
                    >
                      <CopyOutlined style={{ marginRight: '4px' }} />
                      {t('visitors.guestPass.copy')}
                    </button>
                  </div>

                  {guestPassData.PIN && (
                    <div style={{ marginTop: '8px' }}>
                      {t('visitors.guestPass.pin')}: {guestPassData.PIN}
                    </div>
                  )}
                </div>
              </OpList.Item>
            )}
          />
          {hasGuestpassWrite && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '16px',
              }}
            >
              <OpButton
                type="primary"
                onClick={() => {
                  setIsGenerateGuestPassModalOpen(true);
                }}
                style={{
                  width: '160px',
                  padding: '4px 0',
                  marginRight: '16px',
                }}
              >
                {t('visitors.guestPass.edit')}
              </OpButton>
              <OpButton
                type="primary"
                danger
                onClick={handleRevoke}
                style={{
                  width: '160px',
                  padding: '4px 0',
                }}
              >
                {t('visitors.guestPass.revoke')}
              </OpButton>
            </div>
          )}
        </OpForm>
      )}

      {isGenerateGuestPassModalOpen && (
        <OpModal
          title={
            guestPassData
              ? t('visitors.guestPass.edit')
              : t('visitors.guestPass.generate')
          }
          open={isGenerateGuestPassModalOpen}
          width={720}
          onOk={() => {
            generateguestPassForm
              .validateFields()
              .then(() => {
                generateguestPassForm.submit();
                setIsGenerateGuestPassModalOpen(false);
              })
              .catch(() => {
                notificationService.show(
                  'error',
                  t('visitors.guestPass.validationError'),
                );
              });
          }}
          onCancel={finalOnCancel}
          okText={t('visitors.guestPass.ok')}
          cancelText={t('visitors.guestPass.cancel')}
          centered
        >
          <OpForm
            form={generateguestPassForm}
            onSubmit={handleSubmit}
            hasError={false}
            defaultButtons={false}
            initialValues={
              guestPassData
                ? {
                    startUtc: guestPassData.startUtc,
                    endUtc: guestPassData.endUtc,
                    entryId: guestPassData.msiAltaEntryId.map(
                      (entry) => entry.id,
                    ),
                    guestPassPINDefault: !(
                      guestPassData.PIN === null || guestPassData.PIN === ''
                    ),
                    autoSignIn: Boolean(guestPassData.autoSignIn),
                  }
                : {
                    startUtc: defaultGuestPassTime.startUtc,
                    endUtc: defaultGuestPassTime.endUtc,
                    entryId: defaultEntryIds,
                    guestPassPINDefault,
                  }
            }
          >
            <OpForm.DatePicker
              format={DATE_TIME_AM_PM_FORMAT}
              showTime
              minuteStep={5}
              label={t('visitors.guestPass.validFrom')}
              name="startUtc"
              rules={[
                {
                  required: true,
                  message: t('visitors.guestPass.startDateRequired'),
                },
              ]}
              onChange={
                !guestPassData
                  ? (value) => {
                      if (value) {
                        const endUtcValue = opTime(value).add(1, 'hour');
                        generateguestPassForm.setFieldsValue({
                          endUtc: endUtcValue,
                        });
                      }
                    }
                  : undefined
              }
            />
            <OpForm.DatePicker
              format={DATE_TIME_AM_PM_FORMAT}
              showTime
              minuteStep={5}
              label={t('visitors.guestPass.validTo')}
              name="endUtc"
              rules={[
                {
                  required: true,
                  message: t('visitors.guestPass.endDateRequired'),
                },
              ]}
            />
            <OpForm.Select
              name={'entryId'}
              label={t('visitors.guestPass.entries')}
              placeholder={t('visitors.guestPass.entriesPlaceholder')}
              mode="multiple"
              options={entryIdOptions}
              rules={[
                {
                  required: true,
                  message: t('visitors.guestPass.entriesRequired'),
                },
              ]}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                marginBottom: '24px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <OpForm.Item
                  name="guestPassPINDefault"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <OpSwitch />
                </OpForm.Item>
                <span style={{ marginLeft: 8 }}>
                  {t('visitors.guestPass.pinToggle')}
                </span>
              </div>
            </div>
            <div
              style={{ display: 'flex', alignItems: 'center', width: '100%' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <OpForm.Item
                  name="autoSignIn"
                  valuePropName="checked"
                  style={{
                    marginBottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <OpSwitch
                    onChange={(checked: boolean) => {
                      if (checked) {
                        confirmModal({
                          title: t(
                            'visitors.guestPass.confirmAutoSignIn.title',
                          ),
                          content: t(
                            'visitors.guestPass.confirmAutoSignIn.message',
                          ),
                          okText: t('visitors.guestPass.confirmAutoSignIn.ok'),
                          cancelText: t(
                            'visitors.guestPass.confirmAutoSignIn.cancel',
                          ),
                          onOk: () =>
                            generateguestPassForm.setFieldsValue({
                              autoSignIn: true,
                            }),
                          onCancel: () =>
                            generateguestPassForm.setFieldsValue({
                              autoSignIn: false,
                            }),
                        });
                      } else {
                        generateguestPassForm.setFieldsValue({
                          autoSignIn: false,
                        });
                      }
                    }}
                  />
                </OpForm.Item>
                <span style={{ marginLeft: 8 }}>
                  {t('visitors.guestPass.autoSignInToggle')}
                </span>
              </div>
            </div>
          </OpForm>
        </OpModal>
      )}
    </>
  );
};

export default GuestPassTab;
